import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MenuType, LeaderboardMetric } from "@/types/leader-board";
import CircleBg from "@/components/atomics/circle-bg";
import Star from "@/components/atomics/star";
import MotionModal from "@/components/atomics/motion-modal";
import ShareButton from "@/components/atomics/share-button";
import LeaderBoardRankBoost from "./leader-board-boost";
import LeaderBoardRankInfo from "./leader-board-rank-info";
import LeaderBoardShare from "./leader-board-share";
import TruncateAddress from "@/components/utils/truncateAddress";
import {
  METRIC_CONFIG,
  LEADERBOARD_CONSTANTS,
  ROUTE_PATHS,
} from "@/constants/leaderboard";

interface LeaderBoardItemProps {
  rank: number;
  name: string;
  value: number;
  address: string;
  avatar: string;
  indexInCollection?: number;
  selectedMenu: MenuType;
  metric: LeaderboardMetric;
  ownerAddress: string;
  onClick?: () => void;
  onRefetchData?: () => void;
  showShareButton?: boolean;
  onMiddlePartClick?: () => void;
}

const CORNER_SIZE = "5%";
const HOVER_COLOR = "#2B2B2B";
const DEFAULT_COLOR = "#000000";

export default function LeaderBoardItem({
  rank,
  name,
  value,
  address,
  avatar,
  indexInCollection,
  selectedMenu,
  metric,
  ownerAddress,
  onClick,
  onRefetchData,
  showShareButton = false,
  onMiddlePartClick,
}: LeaderBoardItemProps) {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  const formatValue = useCallback(
    (value: number): string => {
      if (selectedMenu === "nft") {
        return value.toString();
      }

      if (metric === "volume") {
        return value.toFixed(2);
      }

      return Math.floor(value).toString();
    },
    [selectedMenu, metric]
  );

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  const handleAvatarClick = useCallback(() => {
    const routePath = ROUTE_PATHS[selectedMenu]?.(address);
    console.log("routePath", routePath);
    if (routePath) {
      navigate(routePath);
    }
  }, [navigate, selectedMenu, address]);

  const handleMetricClick = useCallback(() => {
    setShowModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
  }, []);

  const handleSendStar = useCallback((rating: number) => {
    // This is now handled internally by the LeaderBoardRankBoost component
    setShowModal(false);
  }, []);

  const handleMiddlePartClick = useCallback(() => {
    if (onMiddlePartClick) {
      onMiddlePartClick();
    }
  }, [onMiddlePartClick]);

  const handleShareClick = useCallback(() => {
    setShowShareModal(true);
  }, []);

  const handleCloseShareModal = useCallback(() => {
    setShowShareModal(false);
    if (onMiddlePartClick) {
      onMiddlePartClick();
    }
  }, [onMiddlePartClick]);

  const renderAvatar = () => {
    if (selectedMenu === "users") {
      return (
        <div className="relative w-[66px] h-[66px] opacity-50 hover:cursor-pointer">
          <CircleBg
            className="absolute z-[2] w-[66px] h-[66px] top-[50%] translate-y-[-50%] translate-x-[-50%] left-[50%] rounded-full"
            color={isHovered ? HOVER_COLOR : DEFAULT_COLOR}
          />
          <div className="absolute top-[50%] translate-y-[-50%] left-[50%] translate-x-[-50%] w-[60px] h-[62px]">
            <img
              src={avatar || LEADERBOARD_CONSTANTS.DEFAULT_AVATAR}
              alt={`${name} avatar`}
              className="rounded-full w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>
      );
    } else {
      return (
        <div className="relative w-[66px] h-[66px] opacity-50 hover:cursor-pointer bg-black">
          <img
            src={avatar || LEADERBOARD_CONSTANTS.DEFAULT_AVATAR}
            alt={`${name} collection`}
            className="w-full aspect-square object-cover"
            loading="lazy"
          />
          {Array.from({ length: 4 }, (_, index) => (
            <div
              key={index}
              className={`absolute w-[${CORNER_SIZE}] aspect-square ${
                index === 0
                  ? "top-0 left-0"
                  : index === 1
                    ? "top-0 right-0"
                    : index === 2
                      ? "bottom-0 left-0"
                      : "bottom-0 right-0"
              }`}
              style={{
                backgroundColor: isHovered ? HOVER_COLOR : DEFAULT_COLOR,
              }}
            />
          ))}
        </div>
      );
    }

    return null;
  };

  const renderMetricValue = () => {
    const config = METRIC_CONFIG[metric];
    if (!config) return null;

    return (
      <div
        className={`text-[20px] flex ${metric === "volume" ? "flex-col leading-[1]" : "gap-3"}  items-center ${config.colorClass} hover:cursor-pointer hover:opacity-80 transition-opacity`}
        onClick={handleMetricClick}
      >
        <span className="font-medium">{formatValue(value)}</span>
        {metric === "all_stars" && (
          <Star className={`w-[24px] h-[23px] ${config.colorClass}`} />
        )}
        {config.suffix && (
          <span
            className={`text-[20px] uppercase font-medium ${metric === "volume" ? "font-bold" : ""}`}
          >
            {config.suffix}
          </span>
        )}
      </div>
    );
  };

  const userInfo = {
    rank,
    name,
    value,
    address,
    avatar: avatar || LEADERBOARD_CONSTANTS.DEFAULT_AVATAR,
    ownerAddress,
    indexInCollection,
  };

  return (
    <div className="relative w-full">
      <div
        className={`${showShareButton ? "w-[calc(100%-88px)]" : "w-full"} flex items-center gap-[15px] justify-between px-[20px] py-[15px] hover:bg-[#2B2B2B] cursor-pointer`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="flex items-center gap-[15px] w-full">
          <div className="relative" onClick={handleAvatarClick}>
            {renderAvatar()}
            <div className="absolute top-[50%] translate-y-[-50%] left-[50%] translate-x-[-50%]">
              <span className="text-[20px] font-bold">{rank}</span>
            </div>
          </div>

          <div
            className="flex flex-col w-full py-[15px] hover:cursor-pointer"
            onClick={handleMiddlePartClick}
          >
            <TruncateAddress
              address={name}
              className={`text-[20px] font-medium  ${showShareButton ? "max-w-[110px]" : "max-w-[155px]"}`}
            />
            {selectedMenu === "nft" && (
              <span
                className="text-[16px] font-medium truncate max-w-[200px]"
                title={indexInCollection?.toString()}
              >
                #{indexInCollection?.toString()}
              </span>
            )}
          </div>
        </div>

        {renderMetricValue()}
      </div>

      {showShareButton && (
        <div className="absolute right-0 top-0 bottom-0 flex items-center sm:p-8 p-6 bg-[#006DB5]">
          <ShareButton
            className="w-[24px] h-[34px] hover:cursor-pointer"
            color={"#ffffff"}
            onClick={handleShareClick}
          />
        </div>
      )}
      <div className="fixed">
        <MotionModal isOpen={showModal}>
          {metric === "all_stars" ? (
            <LeaderBoardRankBoost
              userInfo={userInfo}
              selectedMenu={selectedMenu}
              metric={metric}
              onClickClose={handleCloseModal}
              onRefetchData={onRefetchData}
            />
          ) : (
            <LeaderBoardRankInfo
              userInfo={userInfo}
              selectedMenu={selectedMenu}
              metric={metric}
              onClickSendStar={handleSendStar}
              onClickClose={handleCloseModal}
            />
          )}
        </MotionModal>

        <MotionModal isOpen={showShareModal}>
          <LeaderBoardShare
            userInfo={userInfo}
            initialSelectedMenu={selectedMenu}
            initialMetric={metric}
            onClickClose={handleCloseShareModal}
          />
        </MotionModal>
      </div>
    </div>
  );
}
